import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { useVisitStore } from '@/stores/visitStore';

const TimerStrip = () => {
  // visitStore has no `pausedTime`/`isPaused` — the real fields are
  // `visitStatus` ('in-progress' | 'paused' | ...) and `startTime`
  // (an ISO STRING, not epoch ms — see NaN fix in the effect below).
  const { startTime, visitStatus } = useVisitStore();
  const isPaused = visitStatus === 'paused';
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [faceToFaceSeconds, setFaceToFaceSeconds] = useState(0);

  // Calculate CPT code based on time
  const getCPTCode = () => {
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes < 15) return '99213';
    if (minutes < 25) return '99214';
    return '99215';
  };

  // Calculate CPT tier label
  const getCPTLabel = () => {
    const code = getCPTCode();
    const tiers = {
      '99213': 'Tier 2 (15 min)',
      '99214': 'Tier 3 (25 min)',
      '99215': 'Tier 4 (40+ min)',
    };
    return tiers[code] || 'Not Billable';
  };

  // Update timer every second
  useEffect(() => {
    let interval;

    if (!isPaused && startTime) {
      // startTime is an ISO string (visitStore stores toISOString()).
      // `Date.now() - "2026-…"` is NaN — that's why the strip showed
      // "NaN:NaN" for the whole visit. Parse to epoch ms first.
      const startMs = new Date(startTime).getTime();
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.max(0, Math.floor((now - startMs) / 1000));
        setTotalSeconds(elapsed);
        // For demo, face-to-face time = total time (in production would track separately)
        setFaceToFaceSeconds(elapsed);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [startTime, isPaused]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700 px-6 py-3">
      <div className="flex items-center justify-between">
        {/* Left: LIVE indicator and total time */}
        <div className="flex items-center gap-6">
          {/* LIVE Indicator */}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-500 font-bold text-sm uppercase tracking-wider">Live</span>
          </div>

          {/* Total Time */}
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">Total Time</span>
            <span className="text-white font-mono font-bold text-lg">{formatTime(totalSeconds)}</span>
          </div>

          {/* Face-to-Face Time */}
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">F2F</span>
            <span className="text-white font-mono font-bold text-lg">
              {formatTime(faceToFaceSeconds)}
            </span>
          </div>
        </div>

        {/* Center: CPT Tier */}
        <div className="flex items-center gap-2 px-4 py-1 bg-slate-800/50 rounded-lg border border-slate-700">
          <span className="text-slate-400 text-xs uppercase tracking-wider">CPT Tier</span>
          <span className="text-blue-400 font-mono font-semibold text-sm">{getCPTLabel()}</span>
        </div>

        {/* Right: Ambient AI Badge */}
        <div className="flex items-center gap-2 px-3 py-1 bg-purple-500/10 rounded-lg border border-purple-500/30">
          <Zap className="w-4 h-4 text-purple-400" />
          <span className="text-purple-300 text-xs uppercase tracking-wider font-semibold">
            Ambient AI
          </span>
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
        </div>
      </div>
    </div>
  );
};

export default TimerStrip;
